import { useRouter } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui/layout/layout';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import { usePanelToggle } from '@/features/nav-rail/nav-rail.hooks';
import { tabStore } from '@/features/tabs/tabs.store';
import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { inElectron, isMac } from '@/utils';

/** Navbar's left-half button group. Hidden on web. */
export const WindowNavigationControl = observer(() => {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const panelToggle = usePanelToggle();

  if (!inElectron()) return null;

  const goBack = () => {
    const route = tabStore.goBack();
    if (route)
      router.navigate({
        to: route.path,
        search: route.search as Record<string, string>,
      });
  };

  const goForward = () => {
    const route = tabStore.goForward();
    if (route)
      router.navigate({
        to: route.path,
        search: route.search as Record<string, string>,
      });
  };

  return (
    <XStack
      ref={containerRef}
      className={cn(
        'items-center gap-1 h-full pl-3 pr-3',
        (!isMac() || settingsStore.isMainWindowFullScreen) && 'pl-4'
      )}
    >
      <RoundIconButton
        icon={Icons.back}
        label="Back"
        size="sm"
        disabled={!tabStore.canGoBack}
        onClick={goBack}
        className="pointer-events-auto"
      />
      <RoundIconButton
        icon={Icons.navigateRight}
        label="Forward"
        size="sm"
        disabled={!tabStore.canGoForward}
        onClick={goForward}
        className="pointer-events-auto"
      />
      <RoundIconButton
        icon={Icons.sidebar}
        label="Toggle Sidebar"
        size="sm"
        disabled={!panelToggle.available}
        onClick={panelToggle.toggle}
        className="pointer-events-auto"
      />
    </XStack>
  );
});
